/**
 * Our People — interactive behaviour
 *
 * Desktop (> 768 px):
 *   - "More Info" opens a shared detail panel per location (above the grid).
 *   - Clicking the same active person or the × close button collapses the panel.
 *   - Clicking a different person within the same location updates the panel content.
 *   - Only one panel is open at a time across all locations.
 *   - ESC closes any open panel.
 *   - Keyboard accessible: Enter/Space trigger buttons, focus returns to the
 *     activating button when the panel closes.
 *
 * Mobile (≤ 768 px):
 *   - Each person has an inline accordion that toggles independently.
 *   - The shared detail panel is hidden via CSS on mobile.
 *
 * Tooltips:
 *   - Contact icon links show a CSS tooltip via data-tooltip + ::after.
 *   - An additional aria-describedby tooltip element provides screen-reader support.
 */

(function ($) {
  "use strict";

  var MOBILE_BP = 768;

  // ── Helpers ────────────────────────────────────────────────────────────────

  function isMobile() {
    return window.innerWidth <= MOBILE_BP;
  }

  /**
   * Read all data-* attributes from a person article element.
   * Returns a plain object: { name, position, email, officePhone, cell,
   *                            details (array of {title, content}), photo }
   */
  function getPersonData($person) {
    var rawDetails = $person.attr("data-details");
    var details = [];

    if (rawDetails) {
      try {
        details = JSON.parse(rawDetails);
      } catch (e) {
        details = [];
      }
    }

    return {
      name: $person.data("name") || "",
      position: $person.data("position") || "",
      email: $person.data("email") || "",
      officePhone: $person.data("office-phone") || "",
      cell: $person.data("cell") || "",
      details: details,
      photo: $person.data("photo") || "",
    };
  }

  /**
   * Build the HTML string for the shared desktop detail panel inner content.
   */
  function buildPanelHTML(d) {
    var html = '<div class="our-people__panel-left">';

    // Name + position
    html += '<p class="our-people__panel-name">' + escHtml(d.name) + "</p>";
    if (d.position) {
      html +=
        '<p class="our-people__panel-position">' + escHtml(d.position) + "</p>";
    }

    // Contact block
    html +=
      '<p class="our-people__panel-contact-label">Contact Information</p>';

    if (d.officePhone) {
      html +=
        '<div class="our-people__panel-contact-row">' +
        '<span class="our-people__contact-key">Office:</span>' +
        '<a href="tel:' +
        escAttr(cleanPhone(d.officePhone)) +
        '">' +
        escHtml(d.officePhone) +
        "</a></div>";
    }
    if (d.cell) {
      html +=
        '<div class="our-people__panel-contact-row">' +
        '<span class="our-people__contact-key">Cell:</span>' +
        '<a href="tel:' +
        escAttr(cleanPhone(d.cell)) +
        '">' +
        escHtml(d.cell) +
        "</a></div>";
    }
    if (d.email) {
      html +=
        '<div class="our-people__panel-contact-row">' +
        '<span class="our-people__contact-key">Email:</span>' +
        '<a href="mailto:' +
        escAttr(d.email) +
        '">' +
        escHtml(d.email) +
        "</a></div>";
    }

    html += "</div>"; // .our-people__panel-left

    // Right column — repeater detail rows (only non-empty rows passed from PHP)
    html += '<div class="our-people__panel-right">';

    if (d.details && d.details.length) {
      for (var i = 0; i < d.details.length; i++) {
        var row = d.details[i];
        html +=
          "<p><strong>" +
          escHtml(row.title) +
          "</strong> " +
          escHtml(row.content) +
          "</p>";
      }
    }

    html += "</div>"; // .our-people__panel-right

    return html;
  }

  /** Strip non-numeric chars for tel: links (preserves leading +). */
  function cleanPhone(phone) {
    return phone.replace(/[^0-9+]/g, "");
  }

  /** Minimal HTML-escape for injected text content. */
  function escHtml(str) {
    return $("<div>").text(str).html();
  }

  /** Minimal attribute-escape. */
  function escAttr(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  // ── Desktop panel logic ────────────────────────────────────────────────────

  /**
   * Track the currently active button so focus can be restored on close.
   */
  var $activeBtn = null;

  function openPanel($panel, $btn, $person) {
    var data = getPersonData($person);

    // Populate inner content
    $panel.find(".our-people__panel-inner").html(buildPanelHTML(data));

    // Show panel
    $panel.removeAttr("hidden");

    // Mark button active
    $btn.attr("aria-expanded", "true");
    $activeBtn = $btn;

    // Move focus to the close button inside the panel for keyboard users
    $panel.find(".our-people__panel-close").trigger("focus");
  }

  function closePanel($panel) {
    $panel.attr("hidden", "");

    // Reset all More Info buttons within this location
    var location = $panel.closest(".our-people__location");
    location.find(".our-people__more-btn").attr("aria-expanded", "false");

    // Return focus to activating button
    if ($activeBtn && $activeBtn.length) {
      $activeBtn.trigger("focus");
      $activeBtn = null;
    }
  }

  function closeAllPanels() {
    $(".our-people__detail-panel").each(function () {
      if (!$(this).attr("hidden")) {
        closePanel($(this));
      }
    });
  }

  function handleDesktopMoreInfo($btn) {
    var location = $btn.data("location");
    var $location = $btn.closest(".our-people__location");
    var $panel = $("#op-panel-" + location);
    var $person = $btn.closest(".our-people__person");
    var isPanelOpen = !$panel.attr("hidden");
    var isSameBtn = $activeBtn && $activeBtn.is($btn);

    // Close all other location panels first
    $(".our-people__detail-panel")
      .not($panel)
      .each(function () {
        if (!$(this).attr("hidden")) {
          closePanel($(this));
        }
      });

    if (isPanelOpen && isSameBtn) {
      // Toggle: same person → collapse
      closePanel($panel);
    } else {
      // Open / update panel for this person
      // Reset expanded state for sibling buttons in this location
      $location.find(".our-people__more-btn").attr("aria-expanded", "false");
      openPanel($panel, $btn, $person);
    }
  }

  // ── Mobile accordion logic ─────────────────────────────────────────────────

  function handleMobileMoreInfo($btn) {
    var $person = $btn.closest(".our-people__person");
    var $accordion = $person.find(".our-people__accordion");
    var isOpen = !$accordion.attr("hidden");

    if (isOpen) {
      $accordion.attr("hidden", "");
      $btn.attr("aria-expanded", "false");
    } else {
      $accordion.removeAttr("hidden");
      $btn.attr("aria-expanded", "true");
    }
  }

  // ── Tooltip ARIA enhancement ───────────────────────────────────────────────

  /**
   * The CSS tooltip handles visual display. This adds an aria-describedby
   * tooltip element for screen readers that don't read ::after pseudo-content.
   */
  function initTooltipAria() {
    $(".our-people__icon-link[data-tooltip]").each(function (i) {
      var $link = $(this);
      var tooltipId = "op-tooltip-" + i;
      var text = $link.attr("data-tooltip");

      // Create visually-hidden tooltip span
      var $tip = $("<span>", {
        id: tooltipId,
        role: "tooltip",
        class: "show-for-sr",
        text: text,
      });

      $link.after($tip).attr("aria-describedby", tooltipId);
    });
  }

  // ── Bind events ───────────────────────────────────────────────────────────

  function bindEvents() {
    // More Info button
    $(document).on("click", ".our-people__more-btn", function () {
      if (isMobile()) {
        handleMobileMoreInfo($(this));
      } else {
        handleDesktopMoreInfo($(this));
      }
    });

    // Close panel (×) button
    $(document).on("click", ".our-people__panel-close", function () {
      var $panel = $(this).closest(".our-people__detail-panel");
      closePanel($panel);
    });

    // ESC key closes any open panel
    $(document).on("keydown", function (e) {
      if (e.key === "Escape") {
        closeAllPanels();
      }
    });

    // On resize: if switching to desktop, collapse all open accordions to
    // keep state consistent (panel may now be the active mechanism).
    var resizeTimer;
    $(window).on("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (!isMobile()) {
          $(".our-people__accordion").attr("hidden", "");
          $(".our-people__more-btn").attr("aria-expanded", "false");
        }
      }, 200);
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  $(document).ready(function () {
    if ($(".our-people").length === 0) {
      return;
    }
    bindEvents();
    initTooltipAria();

    // Auto-open employee when arriving from ?open=slug (e.g. search results or /employee/ redirect)
    var urlParams = new URLSearchParams(window.location.search);
    var openSlug = urlParams.get("open");
    if (openSlug) {
      var $person = $('[data-slug="' + openSlug + '"]');
      if ($person.length) {
        $person.find(".our-people__more-btn").trigger("click");
        setTimeout(function () {
          var $target = isMobile()
            ? $person
            : $person.closest(".our-people__location");
          if ($target.length) {
            var top = $target.offset().top - 120;
            $("html, body").animate({ scrollTop: top }, 400);
          }
        }, 150);
      }
    }
  });
})(jQuery);
